// ---------------------------------------------------------------------------
// Benchmarks v0 — Mock 数据层
//
// 跨 team 百分位聚合、CPC 原生、CTR/CPM/CVR/ROAS/MER 路标数据全部来自此文件，
// 等待后端 benchmakrs 聚合任务上线后再替换为真实 GET /api/v1/benchmarks 接口。
// 产品价值验证阶段，mock 数据结构保持与 spec 一致，替换成本 ≤ 2h。
// ---------------------------------------------------------------------------

import type { MaybeRef } from 'vue'

import { useQuery } from '@tanstack/vue-query'
import { computed, onBeforeUnmount, onMounted, ref, unref } from 'vue'

import { useTeam } from '@/composables/use-team'
import { useNcCpaQuery } from '@/services/api/nc-cpa.api'
import { useUserStore } from '@/stores/user'

export type BenchmarkStatus
  = 'ok'
    | 'insufficient_user_data'
    | 'insufficient_sample'
    | 'roadmap'
    | 'stale_data'

export type BenchmarkReleaseStatus = 'released' | 'roadmap'
export type BenchmarkChannelType = 'google' | 'meta' | 'tiktok' | 'bing'

export interface BenchmarkChannelRow {
  channel_type: BenchmarkChannelType
  channel_name: string
  channel_color: string
  blurred: boolean
  user_value: number | null
  p25: number
  /** 渠道中位值来自接口 nccpa_aov；接口未匹配到该渠道时为 null（展示「—」） */
  p50: number | null
  p75: number
  sample_n: number
}

export interface BenchmarkMetric {
  metric_key:
    | 'nc_cpa'
    | 'cpc'
    | 'ctr'
    | 'cpm'
    | 'cvr'
    | 'roas'
    | 'mer'
  display_name: string
  release_status: BenchmarkReleaseStatus
  attribution_hint: string
  unit: 'usd' | 'ratio' | 'percent'
  decimals: number

  status: BenchmarkStatus
  cohort_name: string
  sample_n: number
  updated_at_display: string

  user_value: number | null
  user_percentile: number | null
  p10: number
  p25: number
  p50: number
  p75: number
  p90: number

  channel_breakdown: BenchmarkChannelRow[]
}

export interface BenchmarksData {
  team_id: number
  user_cohort_name: string
  user_aov_usd: number | null
  total_teams_pooled: number
  billing_plan: 'free' | 'pro' | 'enterprise'
  metrics: BenchmarkMetric[]
}

export type BenchmarkViewScope = 'home' | 'detail'

/**
 * URL param billing-plan override for UX walkthrough.
 *
 * REACTIVE SESSION-SCOPED STATE (fixes root cause of ?plan=pro "not working"
 * after hashchange / login→home redirect):
 *   - urlOverridePlan() is a pure function on non-reactive window.location.
 *     If you first render benchmarks at /?plan=free#/login then router pushes
 *     to #/home, the cached computed never re-runs because location.href
 *     isn't a Vue reactive. So we:
 *       (1) Cache parsed plan to sessionStorage key 'benchmarks:plan_override'
 *           so ANY sub-page / router push keeps the override you set.
 *       (2) Expose a module-level reactive ref `_planOverrideRef` that holds
 *           the parsed value.
 *       (3) listen to hashchange + popstate on first useBenchmarksQuery mount
 *           and re-sync _planOverrideRef whenever the URL changes.
 *       (4) computed() in useBenchmarksQuery reads _planOverrideRef so
 *           Vue's reactivity fires when hash/popstate re-syncs.
 *
 * URL positions supported (ANY of these works):
 *   /cherry/?plan=pro#/benchmarks    (standard search, before #)
 *   /cherry/#/benchmarks?plan=pro     (hash-internal, after #)
 *   /cherry/?plan=pro#/home           (login push-state then home push still carries session cache)
 * Remove ?plan → fall back to localStorage plan cache deletion → fall back to team/user plan.
 */
function _parseUrlPlan(): BenchmarksData['billing_plan'] | null {
  if (typeof window === 'undefined')
    return null
  const search = (k: string): string | null => {
    try {
      return new URL(window.location.href).searchParams.get(k)
    }
    catch {
      return null
    }
  }
  const hash = (k: string): string | null => {
    try {
      const h = window.location.hash || ''
      const idx = h.indexOf('?')
      if (idx === -1)
        return null
      return new URLSearchParams(h.slice(idx + 1)).get(k)
    }
    catch {
      return null
    }
  }
  for (const v of [search('plan'), hash('plan')]) {
    const s = String(v || '').toLowerCase()
    if (s === 'free' || s === 'pro' || s === 'enterprise')
      return s as any
  }
  return null
}

function _writeSessionCache(val: BenchmarksData['billing_plan'] | null): void {
  try {
    if (val)
      sessionStorage.setItem('benchmarks:plan_override', val)
    else
      sessionStorage.removeItem('benchmarks:plan_override')
  }
  catch {}
}

function _readSessionCache(): BenchmarksData['billing_plan'] | null {
  try {
    const v = sessionStorage.getItem('benchmarks:plan_override')
    if (v === 'free' || v === 'pro' || v === 'enterprise')
      return v
    return null
  }
  catch {
    return null
  }
}

const _planOverrideRef = ref<BenchmarksData['billing_plan'] | null>(null)

function _syncPlanOverride(): void {
  const parsed = _parseUrlPlan()
  // Case 1: URL has explicit ?plan= → always write cache + reactive ref
  if (parsed) {
    _writeSessionCache(parsed)
    _planOverrideRef.value = parsed
    return
  }
  // Case 2: URL has no ?plan= → read from session cache (from earlier same-tab load,
  // typically /login?plan=pro → router pushes #/home and drops search params).
  // If cache exists AND reactive ref isn't yet set, keep the cached value.
  if (_planOverrideRef.value === null) {
    const cached = _readSessionCache()
    if (cached)
      _planOverrideRef.value = cached
    return
  }
  // Case 3: ref currently has an override but URL no longer has ?plan= → user
  // manually removed ?plan= param; clear both ref and session cache.
  _writeSessionCache(null)
  _planOverrideRef.value = null
}

// Sync TWICE on initial load so login→home never loses URL override:
//   (1) immediately on module import (e.g. main.ts eager import)
//   (2) on window load (catches late modules lazy-imported after initial render)
if (typeof window !== 'undefined') {
  _syncPlanOverride()
  window.addEventListener('load', _syncPlanOverride, { once: true })
  if (document.readyState === 'complete')
    _syncPlanOverride()
}

let _routerListenersInstalled = false
function ensureRouterListeners(): void {
  if (typeof window === 'undefined' || _routerListenersInstalled)
    return
  _routerListenersInstalled = true
  addEventListener('hashchange', _syncPlanOverride)
  addEventListener('popstate', _syncPlanOverride)
  // Re-sync immediately on first mount (catches late-imported modules, e.g.
  // benchmarks module being loaded for the first time on the Home page via
  // BenchmarksHomeCard lazy mount — by then the URL already has ?plan=pro).
  _syncPlanOverride()
}

/**
 * Shared composable — exposed so HomeCard.vue / pages/benchmarks/index.vue
 * read the SAME reactive plan override source + session cache + router
 * listeners as useBenchmarksQuery. No duplicated URL parsing.
 *
 * Rules:
 *   (1) URL ?plan= (either search OR hash-internal) sets override + session cache
 *   (2) Router pushes that lose URL params (e.g. login→home) still carry the plan
 *       because sessionStorage 'benchmarks:plan_override' keeps it for the tab session.
 *   (3) Manual removal of ?plan= param from current URL → clears cache → fallback real team plan.
 *   (4) fallback: userStore.isFree() (localStorage app-shopify-vuex plan).
 */
export function useBillingPlanOverride() {
  onMounted(() => ensureRouterListeners())
  onBeforeUnmount(() => {})

  const userStore = useUserStore()

  const billingPlan = computed<'free' | 'pro' | 'enterprise'>(() => {
    const override = _planOverrideRef.value
    if (override)
      return override
    return userStore.isFree() ? 'free' : 'pro'
  })

  const isFree = computed(() => billingPlan.value.includes('free'))

  return { billingPlan, isFree }
}

const PEER_DISTRIBUTION_S2: Record<
  Exclude<BenchmarkMetric['metric_key'], 'cvr' | 'roas' | 'mer'>,
  { p10: number, p25: number, p50: number, p75: number, p90: number }
> = {
  nc_cpa: { p10: 28.2, p25: 41.0, p50: 78.0, p75: 125.5, p90: 182.0 },
  cpc: { p10: 0.45, p25: 0.68, p50: 1.12, p75: 1.85, p90: 2.78 },
  ctr: { p10: 0.28, p25: 0.52, p50: 0.95, p75: 1.68, p90: 2.45 },
  cpm: { p10: 2.15, p25: 3.60, p50: 6.40, p75: 10.25, p90: 15.80 },
}

const CHANNEL_DIST_P25_P75_S2: Record<
  BenchmarkChannelType,
  { nc_cpa: [number, number, number], cpc: [number, number, number] }
> = {
  google: { nc_cpa: [52, 82, 132], cpc: [0.72, 1.18, 1.95] },
  meta: { nc_cpa: [38, 62, 98], cpc: [0.52, 0.88, 1.45] },
  tiktok: { nc_cpa: [58, 94, 148], cpc: [0.85, 1.32, 2.10] },
  bing: { nc_cpa: [42, 68, 108], cpc: [0.60, 0.98, 1.62] },
}

const CHANNEL_META: Record<BenchmarkChannelType, { name: string, color: string }> = {
  google: { name: 'Google Ads', color: '#1a73e8' },
  meta: { name: 'Meta', color: '#1877f2' },
  tiktok: { name: 'TikTok', color: '#010101' },
  bing: { name: 'Bing', color: '#008373' },
}

/**
 * 前端渠道类型 -> 接口 channel_type 维度返回的 key。
 * 后端 meta 渠道在 results 里的 key 是 `facebook`，其余同名。
 */
const CHANNEL_API_KEY: Record<BenchmarkChannelType, string> = {
  google: 'ad_google',
  meta: 'facebook',
  tiktok: 'tiktok',
  bing: 'bing',
}

// ---------------------------------------------------------------------------
// Cohort 分群（v0：AOV 单向非等距 4+2 段）
// 参考 .trae/documents/benchmark-nc-cpa-v0-plan.md 第 3.2 节。
// 边界：S1 <$55 / S2 $55–130 / S3 $130–260 / S4a $260–600 / S4b >$600。
// v0 先写死：段判定由真实 AOV 决定，但各段 peer 分布暂统一沿用 PEER_DISTRIBUTION_S2，
// 等后端 suggestion_evidence 按 cohort 下发分位数后再替换。
// ---------------------------------------------------------------------------

export type AovSegmentId = 's1' | 's2' | 's3' | 's4a' | 's4b'

const AOV_SEGMENTS: Array<{
  id: AovSegmentId
  low: number
  high: number
  cohort_name: string
}> = [
  { id: 's1', low: 0, high: 55, cohort_name: 'Stores with < $55 AOV' },
  { id: 's2', low: 55, high: 130, cohort_name: 'Stores with $55–$130 AOV' },
  { id: 's3', low: 130, high: 260, cohort_name: 'Stores with $130–$260 AOV' },
  { id: 's4a', low: 260, high: 600, cohort_name: 'Stores with $260–$600 AOV' },
  { id: 's4b', low: 600, high: Infinity, cohort_name: 'Stores with $600+ AOV' },
]

const DEFAULT_AOV_SEGMENT = AOV_SEGMENTS[1] // S2：v0 核心主力段，AOV 缺失时兜底

/** 由真实 AOV 判定落哪个 cohort 段；AOV 为 null/未知 -> 兜底 S2。 */
function resolveAovSegment(aov: number | null | undefined): typeof AOV_SEGMENTS[number] {
  if (aov == null || !Number.isFinite(aov) || aov <= 0)
    return DEFAULT_AOV_SEGMENT
  return AOV_SEGMENTS.find(s => aov >= s.low && aov < s.high) ?? DEFAULT_AOV_SEGMENT
}

/**
 * Mock 驱动的 benchmarks 查询 composable。
 * v0 首期：
 *   - nc_cpa:   真实走 /api/v1/nc_cpa/query（失败回退 mock）+ 百分位来自静态 AOV s2 分布
 *   - cpc:      全 mock（接口未出）
 *   - ctr/cpm/cvr/roas/mer: roadmap 状态
 */
export function useBenchmarksQuery(
  viewScope: MaybeRef<BenchmarkViewScope>,
  opts?: { forceMock?: boolean },
) {
  onMounted(() => ensureRouterListeners())
  onBeforeUnmount(() => {})

  const scope = computed(() => unref(viewScope))
  const forceMock = computed(() => opts?.forceMock ?? false)

  const ncGlobal = useNcCpaQuery(
    computed(() => ({ rollup_dim_key: 'global', window: '28d' as const })),
  )
  const ncChannel = useNcCpaQuery(
    computed(() => ({ rollup_dim_key: 'platform', window: '28d' as const })),
  )

  const { currentTeam } = useTeam()
  const teamId = computed(() => currentTeam.value?.id ?? Number(sessionStorage.getItem('team_id') || 0))
  const billingPlan = computed<BenchmarksData['billing_plan']>(() => {
    const override = _planOverrideRef.value
    if (override)
      return override
    const p = (currentTeam.value as any)?.plan
      || JSON.parse(localStorage.getItem(`app-shopify-vuex_${teamId.value}`) || '{}')?.user?.sub_items_price?.plan?.item_plan
      || 'free'
    if (String(p).toLowerCase().includes('enterprise'))
      return 'enterprise'
    if (String(p).toLowerCase().includes('pro'))
      return 'pro'
    return 'free'
  })

  return useQuery({
    // billingPlan MUST be part of queryKey so URL ?plan= toggle invalidates cache
    // (otherwise ?plan=free → ?plan=pro keeps serving the cached blurred/free result).
    queryKey: computed(() => ['benchmarks', scope.value, teamId.value, forceMock.value, billingPlan.value]),
    queryFn: async () => {
      try {
        await ncGlobal.suspense?.()
      }
      catch {}
      try {
        await ncChannel.suspense?.()
      }
      catch {}
      return buildBenchmarks(
        scope.value,
        teamId.value,
        billingPlan.value,
        unref(ncGlobal.data),
        unref(ncChannel.data),
      )
    },
    staleTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
  })
}

// ---------- builders ----------

export function buildBenchmarks(
  scope: BenchmarkViewScope,
  teamId: number,
  billingPlan: BenchmarksData['billing_plan'],
  ncGlobal: ReturnType<typeof useNcCpaQuery>['data']['value'],
  ncChannel: ReturnType<typeof useNcCpaQuery>['data']['value'],
): BenchmarksData {
  const updatedAtDisplay = `Updated Aug 18 (Mon) 04:00`

  // Your user NC CPA value —— 只用接口真值；接口无数据(null)时 user_value=null（卡片展示「—」）
  const userNcCpaGlobal = ncGlobal?.results?.all?.nc_cpa ?? null

  // Cohort 分群：用接口返回的真实 AOV 判定落哪段（S1~S4b），AOV 缺失兜底 S2。
  const userAov = ncGlobal?.results?.all?.aov ?? null
  const cohortSegment = resolveAovSegment(userAov)
  const cohortName = cohortSegment.cohort_name

  // 全局 NC CPA 同行分布（p10~p90，决定 benchmark 色带与区间）改为读取接口 all.nccpa_distribute 真值；
  // 接口缺失时兜底静态 mock。p50 优先取分布里的 p50，其次单独的 nccpa_aov，最后兜底 mock。
  const globalDist = ncGlobal?.results?.all?.nccpa_distribute
  const globalNcCpaDist = globalDist
    ? {
        p10: globalDist.p10,
        p25: globalDist.p25,
        p50: globalDist.p50,
        p75: globalDist.p75,
        p90: globalDist.p90,
      }
    : {
        ...PEER_DISTRIBUTION_S2.nc_cpa,
        p50: ncGlobal?.results?.all?.nccpa_aov ?? PEER_DISTRIBUTION_S2.nc_cpa.p50,
      }

  const ncCpaMetric: BenchmarkMetric = buildReleasedMetric({
    metric_key: 'nc_cpa',
    display_name: 'New Customer CPA',
    attribution_hint: 'Full Impact unified attribution · 30d click + 1d view',
    unit: 'usd',
    decimals: 2,
    dist: globalNcCpaDist,
    user_value: userNcCpaGlobal,
    billingPlan,
    cohortName,
    channelBreakdownBuilder: (channel, blurred) => {
      const [p25, , p75] = CHANNEL_DIST_P25_P75_S2[channel].nc_cpa
      // 渠道 NC CPA 只取接口真值；接口无该渠道数据 -> null（不再用写死 mock 兜底）
      // meta 在接口 results 里的 key 是 facebook，其余同名，统一走 CHANNEL_API_KEY 映射。
      const channelItem = ncChannel?.results?.[CHANNEL_API_KEY[channel]]
      const user_value = !blurred
        ? channelItem?.nc_cpa ?? null
        : null
      // 中位值（p50）只取接口 nccpa_aov 真值；未匹配到该渠道 -> null（展示「—」）。
      const p50 = channelItem?.nccpa_aov ?? null
      return buildChannelRow(channel, blurred, user_value, p25, p50, p75, 186)
    },
    updatedAtDisplay,
  })

  const roadmapMetric = (
    metric_key: BenchmarkMetric['metric_key'],
    display_name: string,
    attribution_hint: string,
    unit: BenchmarkMetric['unit'],
    decimals: number,
  ): BenchmarkMetric => ({
    metric_key,
    display_name,
    release_status: 'roadmap',
    attribution_hint,
    unit,
    decimals,
    status: 'roadmap',
    cohort_name: cohortName,
    sample_n: 0,
    updated_at_display: updatedAtDisplay,
    user_value: null,
    user_percentile: null,
    p10: 0,
    p25: 0,
    p50: 0,
    p75: 0,
    p90: 0,
    channel_breakdown: [],
  })

  const cpcMetric = roadmapMetric('cpc', 'Cost per Click', 'Channel-native · reads directly from Ads reports', 'usd', 2)

  const allMetrics: BenchmarkMetric[] = scope === 'home'
    ? [ncCpaMetric, cpcMetric]
    : [
        ncCpaMetric,
        cpcMetric,
        roadmapMetric('ctr', 'Click-through Rate', 'Channel-native · reads directly from Ads reports', 'percent', 2),
        roadmapMetric('cpm', 'Cost per 1000 Impressions', 'Channel-native · reads directly from Ads reports', 'usd', 2),
        roadmapMetric('cvr', 'Conversion Rate', 'Full Impact unified attribution', 'percent', 2),
        roadmapMetric('roas', 'Return on Ad Spend', 'Full Impact unified attribution · requires COGS', 'ratio', 2),
        roadmapMetric('mer', 'Marketing Efficiency Ratio', 'Full Impact unified attribution · requires COGS', 'ratio', 2),
      ]

  return {
    team_id: teamId,
    user_cohort_name: cohortName,
    user_aov_usd: userAov,
    total_teams_pooled: 248,
    billing_plan: billingPlan,
    metrics: allMetrics,
  }
}

interface BuildReleasedMetricArgs {
  metric_key: BenchmarkMetric['metric_key']
  display_name: string
  attribution_hint: string
  unit: BenchmarkMetric['unit']
  decimals: number
  dist: { p10: number, p25: number, p50: number, p75: number, p90: number }
  user_value: number | null
  channelBreakdownBuilder: (ch: BenchmarkChannelType, blurred: boolean) => BenchmarkChannelRow
  updatedAtDisplay: string
  billingPlan: BenchmarksData['billing_plan']
  cohortName: string
}

function buildReleasedMetric(args: BuildReleasedMetricArgs): BenchmarkMetric {
  const { dist, user_value, billingPlan } = args
  const user_percentile = user_value == null
    ? null
    : Math.round(estimatePercentile(user_value, dist))
  // 暂时去掉 free 计划的渠道打码限制：所有计划均可查看 By channel 明细。

  const isFree = billingPlan === 'free'
  const channels = (['google', 'meta', 'tiktok', 'bing'] as BenchmarkChannelType[]).map(ch =>
    args.channelBreakdownBuilder(ch, false),
  )

  return {
    metric_key: args.metric_key,
    display_name: args.display_name,
    release_status: 'released',
    attribution_hint: args.attribution_hint,
    unit: args.unit,
    decimals: args.decimals,
    status: user_value == null ? 'insufficient_user_data' : 'ok',
    cohort_name: args.cohortName,
    sample_n: 248,
    updated_at_display: args.updatedAtDisplay,
    user_value,
    user_percentile,
    p10: dist.p10,
    p25: dist.p25,
    p50: dist.p50,
    p75: dist.p75,
    p90: dist.p90,
    channel_breakdown: channels,
  }
}

function buildChannelRow(
  channel: BenchmarkChannelType,
  blurred: boolean,
  user_value: number | null,
  p25: number,
  p50: number | null,
  p75: number,
  sample_n: number,
): BenchmarkChannelRow {
  return {
    channel_type: channel,
    channel_name: CHANNEL_META[channel].name,
    channel_color: CHANNEL_META[channel].color,
    blurred,
    user_value: blurred ? null : user_value,
    p25,
    p50,
    p75,
    sample_n,
  }
}

/**
 * Linear-interp percentile estimator against the 5-point peer distribution.
 * Treats P10–P90 as the clipped range of display (map to 5%–95% visually).
 */
function estimatePercentile(value: number, dist: { p10: number, p25: number, p50: number, p75: number, p90: number }): number {
  if (value <= dist.p10)
    return 5 + (value / Math.max(dist.p10, 1e-9)) * 5
  if (value >= dist.p90)
    return 95 + Math.min(5, ((value - dist.p90) / Math.max(dist.p90, 1e-9)) * 5)
  const knots: Array<[number, number]> = [
    [dist.p10, 10],
    [dist.p25, 25],
    [dist.p50, 50],
    [dist.p75, 75],
    [dist.p90, 90],
  ]
  for (let i = 0; i < knots.length - 1; i++) {
    const [x0, y0] = knots[i]
    const [x1, y1] = knots[i + 1]
    if (value >= x0 && value <= x1) {
      const t = (value - x0) / Math.max(x1 - x0, 1e-9)
      return y0 + t * (y1 - y0)
    }
  }
  return 50
}

export interface PublicBenchmarksTeamMeta {
  team_id: number
  team_name: string
  team_web_url: string
  team_currency: string
  team_timezone: string
}

export interface PublicBenchmarksResult {
  team: PublicBenchmarksTeamMeta
  data: BenchmarksData
}

function resolvePublicTeamMeta(teamId: number): PublicBenchmarksTeamMeta {
  // 最高优先级：sessionStorage 中切换店铺用的完整 teams 列表（team_list），
  // 每个元素就是 Team 对象（{ id, name, web_url, currency, timezone, ... }），
  // 和 user-menu.vue 右上角头像左侧"Store"那一行显示的名字同源（user.team_list[].name）。
  try {
    const raw = sessionStorage.getItem('teams')
    if (raw) {
      const list = JSON.parse(raw) as any[]
      const hit = list.find(t => Number(t.id) === teamId)
      if (hit?.name) {
        return {
          team_id: teamId,
          team_name: hit.name,
          team_web_url: hit.web_url || '',
          team_currency: hit.currency || 'USD',
          team_timezone: hit.timezone || 'UTC',
        }
      }
    }
  }
  catch {}
  // v0：本地同 session 可从 sessionStorage 恢复真实 team 名称；
  // 其余情况（真实分享给外部访客）走后端公开接口，目前降级为"Benchmarks Report"占位（永不出现 Store #id 字样）。
  try {
    const stored = sessionStorage.getItem('team')
    const storedId = Number(sessionStorage.getItem('team_id') || 0)
    if (stored && storedId === teamId) {
      const t = JSON.parse(stored) as any
      if (t) {
        return {
          team_id: teamId,
          team_name: t.name || 'Benchmarks Report',
          team_web_url: t.web_url || '',
          team_currency: t.currency || 'USD',
          team_timezone: t.timezone || 'UTC',
        }
      }
    }
  }
  catch {}
  try {
    const tn = sessionStorage.getItem('team_name')
    const storedId = Number(sessionStorage.getItem('team_id') || 0)
    if (tn && storedId === teamId) {
      return {
        team_id: teamId,
        team_name: tn,
        team_web_url: '',
        team_currency: 'USD',
        team_timezone: 'UTC',
      }
    }
  }
  catch {}
  try {
    const vuexKey = `app-shopify-vuex_${teamId}`
    const raw = localStorage.getItem(vuexKey)
    if (raw) {
      const p = JSON.parse(raw) as any
      const t = p?.user?.team
      if (t) {
        return {
          team_id: teamId,
          team_name: t.name || 'Benchmarks Report',
          team_web_url: t.web_url || '',
          team_currency: t.currency || 'USD',
          team_timezone: t.timezone || 'UTC',
        }
      }
    }
  }
  catch {}
  return {
    team_id: teamId,
    team_name: 'Benchmarks Report',
    team_web_url: '',
    team_currency: 'USD',
    team_timezone: 'UTC',
  }
}

export function buildPublicBenchmarks(teamId: number): PublicBenchmarksResult {
  const data = buildBenchmarks('detail', teamId, 'pro', null, null)
  const released = data.metrics.filter(m => m.release_status === 'released')
  const team = resolvePublicTeamMeta(teamId)
  return {
    team,
    data: { ...data, metrics: released },
  }
}

/**
 * 公开 benchmarks 查询。
 * - 不依赖登录态 / token / currentTeam
 * - v0：完全基于 AOV 分位静态分布（不调用需要鉴权的 nc_cpa 接口）
 * - 后端公开分享接口 ready 后，把 queryFn 替换为真实 GET /public/benchmarks?team_id=... 即可
 */
export function usePublicBenchmarksQuery(teamId: MaybeRef<number>) {
  const tid = computed(() => Math.max(0, Number(unref(teamId)) || 0))
  return useQuery({
    queryKey: computed(() => ['public-benchmarks', tid.value]),
    queryFn: () => buildPublicBenchmarks(tid.value),
    staleTime: 1000 * 60 * 60 * 6,
    refetchOnWindowFocus: false,
  })
}
